import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, CheckCircle, ArrowRight, Shield, Loader2, FileText, Brain, Target, Clock, FolderOpen, Users, Zap, BarChart3, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  { icon: FileText, text: "Essay Studio — draft, revise, track versions" },
  { icon: Brain, text: "AI Essay Feedback — instant structural analysis" },
  { icon: BarChart3, text: "College Match Scores — know where you stand" },
  { icon: Target, text: "School List Builder — reach, target, safety" },
  { icon: Clock, text: "Application Timeline — every deadline tracked" },
  { icon: FolderOpen, text: "Document Vault — transcripts, scores, letters" },
  { icon: Users, text: "Recommender Tracker — status & reminders" },
  { icon: Zap, text: "Activity Organizer — Common App format" },
];

export default function PaywallPage() {
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!token) {
      toast.error('You need to sign in before activating the portal.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: 'portal',
          origin_url: window.location.origin,
        }),
      });

      let data = null;
      try { data = await res.json(); } catch { /* non-JSON response */ }

      if (!res.ok) {
        const msg = (data && (data.detail || data.message)) || `Checkout failed (status ${res.status})`;
        console.error('Checkout error:', res.status, data);
        toast.error(msg);
        setLoading(false);
        return;
      }

      const url = data?.checkout_url || data?.url;
      if (url) {
        // Pre-warn if browser likely to block (very rare)
        window.location.href = url;
        return;
      }
      console.error('Checkout missing URL in response:', data);
      toast.error('Stripe did not return a checkout URL. Please try again or contact support.');
      setLoading(false);
    } catch (err) {
      console.error('Checkout network error:', err);
      toast.error(err?.message || 'Network error — please check your connection and try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center px-4" data-testid="paywall-page">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <GraduationCap className="h-8 w-8 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
          </div>
          <h1 className="text-3xl font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Unlock Your <span className="italic text-[#0F392B]">Full Portal</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Your account is created. One step left — activate your portal access.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-[#E2E2E2] shadow-lg overflow-hidden">
          {/* Price */}
          <div className="bg-[#0F392B] p-6 text-center">
            <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-widest mb-2">Full Portal Access</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>$99</span>
              <span className="text-lg text-white/60">/year</span>
            </div>
            <p className="text-xs text-white/50 mt-2">One-time annual payment. Cancel anytime.</p>
          </div>

          {/* Features */}
          <div className="p-6 space-y-3">
            {features.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#0F392B]/5 flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-3.5 w-3.5 text-[#0F392B]" />
                </div>
                <span className="text-sm text-muted-foreground">{f.text}</span>
              </div>
            ))}

            {/* Bonus */}
            <div className="mt-4 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl flex items-start gap-3">
              <Mic className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Bonus: 45-Minute Strategy Session</p>
                <p className="text-xs text-muted-foreground mt-0.5">With a lead strategist or Ivy-level advisor. Included with every purchase.</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-6 pb-6">
            <Button
              className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 text-base font-semibold shadow-lg"
              onClick={handleCheckout}
              disabled={loading}
              data-testid="paywall-checkout-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Redirecting to checkout...</>
              ) : (
                <>Activate Portal — $99/yr <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Shield className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">Secure checkout powered by Stripe</span>
            </div>
          </div>
        </div>

        {/* Logout option */}
        <div className="text-center mt-6">
          <button onClick={logout} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="paywall-logout-btn">
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
}
